import React, { createContext, useContext, ReactNode } from 'react';

export interface Currency {
  code: string;
  symbol: string;
  name: string;
  locale: string;
  position: 'before' | 'after';
  decimalPlaces: number;
  thousandsSeparator: string;
  decimalSeparator: string;
}

export interface CurrencyContextType {
  currency: Currency;
  formatPrice: (amount: number) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// The app displays and charges in a single fixed currency (EUR) - see
// backend/app/currency_utils.py. There is no currency switcher: Stripe only
// ever charges in EUR regardless of what a customer might have selected, so
// letting them pick a different display currency was misleading.
const CURRENCY: Currency = {
  code: 'EUR',
  symbol: '€',
  name: 'Euro',
  locale: 'es-ES',
  position: 'after',
  decimalPlaces: 2,
  thousandsSeparator: '.',
  decimalSeparator: ',',
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const formatPrice = (amount: number): string => {
    try {
      return new Intl.NumberFormat(CURRENCY.locale, {
        style: 'currency',
        currency: CURRENCY.code,
        minimumFractionDigits: CURRENCY.decimalPlaces,
        maximumFractionDigits: CURRENCY.decimalPlaces,
      }).format(amount);
    } catch (error) {
      return `${CURRENCY.symbol}${amount.toFixed(CURRENCY.decimalPlaces)}`;
    }
  };

  const value: CurrencyContextType = {
    currency: CURRENCY,
    formatPrice,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
